"use client";

import { useCallback, useState, useEffect } from "react";
import { QueryClientProvider } from "@tanstack/react-query";
import { getQueryClient } from "~/widgets/lib/query-client";
import { trackConversion } from "~/widgets/lib/tracking";
import { basketModeFor, parseBasketMode } from "@baluu/ui/lib/checkout/basket-surface";
import { CheckoutGiftCard } from "@baluu/ui/components/checkout";
import { StripePaymentPortal } from "../StripePaymentPortal";
import { SuccessScreenPortal } from "../SuccessScreenPortal";
import { TermsDialogPortal } from "../TermsDialogPortal";
import type { SuccessData } from "../PortalManager";
import type { GiftcardCheckoutOverlayProps, StripePaymentData } from "./types";
import type { TermsSettings, CustomTerms } from "@baluu/ui/components/checkout/utils/types";
import type { BasketLine } from "~/widgets/lib/basket-lanes";
import { addPurchaseToBasket, proceedToBasketCheckout } from "~/widgets/lib/purchase-overlay";
import { useBasketItemCount } from "~/widgets/lib/use-basket";
import { useBasketConflict } from "~/widgets/lib/use-basket-conflict";
import { useSchoolCheckoutConfig } from "../PurchaseOverlay";

// Default API URL
const DEFAULT_API_URL = "https://api.baluu.io";

type PortalMode = "checkout" | "stripe" | "success";

/** What the gift card flow hands over (loosely typed there). */
type GiftcardItemData = Partial<BasketLine> & {
  selectedImage?: string;
  guestDetails?: { email?: string };
};

export function GiftcardCheckoutOverlay({
  businessId,
  businessSlug,
  apiUrl = DEFAULT_API_URL,
  primaryColor = "#03c2b9",
  theme: _theme = "light",
  widgetBaseUrl,
  mode = "popup",
  basket,
}: GiftcardCheckoutOverlayProps) {
  const queryClient = getQueryClient();

  // The basket this surface offers, once the school says whether it allows
  // one. Until then (and if its settings can't be had) a gift card is
  // bought on its own, the lane every embed has always taken.
  const schoolSettings = useSchoolCheckoutConfig(businessId, apiUrl);
  const basketMode =
    schoolSettings.status === "ready"
      ? basketModeFor(parseBasketMode(basket), schoolSettings.config.basket?.isEnabled)
      : "off";
  // A refused "Add to basket" / "Proceed to checkout", shown on the flow's
  // choice step until the visitor moves to another step.
  const {
    conflict: basketConflict,
    refuse,
    onStepChange: handleStepChange,
  } = useBasketConflict({ businessId, apiUrl, primaryColor, mode: basketMode });

  // Portal state management
  const [portalMode, setPortalMode] = useState<PortalMode>("checkout");
  const [stripePaymentData, setStripePaymentData] =
    useState<StripePaymentData | null>(null);
  const [successData, setSuccessData] = useState<SuccessData | null>(null);

  // Gift card images
  const [giftCardImages, setGiftCardImages] = useState<string[]>([]);

  // Terms settings from API
  const [termsSettings, setTermsSettings] = useState<TermsSettings | undefined>(undefined);
  const [customTerms, setCustomTerms] = useState<CustomTerms | undefined>(undefined);

  // Terms dialog state for Shadow DOM portal
  const [termsDialogData, setTermsDialogData] = useState<{
    isOpen: boolean;
    termsSettings?: TermsSettings;
    customTerms?: CustomTerms;
    timezone?: string;
  }>({ isOpen: false });

  const [schoolName, setSchoolName] = useState<string>("");

  const basketItemCount = useBasketItemCount(businessId);

  // Fetch gift card data including images and terms settings on mount
  useEffect(() => {
    async function fetchGiftCardData() {
      if (!businessSlug) return;
      try {
        const response = await fetch(
          `${apiUrl}/widget/pages/gift-card/${businessSlug}`
        );
        if (response.ok) {
          const data = await response.json();
          if (data.giftCard?.images?.length > 0) {
            setGiftCardImages(data.giftCard.images);
          }
          // Extract terms settings from school data
          if (data.school?.settings) {
            setTermsSettings({
              termsAndConditions: data.school.settings.termsAndConditions,
              cancellationPolicy: data.school.settings.cancellationPolicy,
              reschedulePolicy: data.school.settings.reschedulePolicy,
            });
          }
          if (data.school?.customTerms) {
            setCustomTerms(data.school.customTerms);
          }
          if (data.school?.name) {
            setSchoolName(data.school.name);
          }
        }
      } catch (error) {
        console.error("[GiftcardCheckout] Failed to fetch gift card data:", error);
      }
    }
    fetchGiftCardData();
  }, [apiUrl, businessSlug]);

  // Handle close
  const handleClose = useCallback(() => {
    // Dispatch close event
    const event = new CustomEvent("baluu:giftcard-checkout-close", {
      bubbles: true,
      composed: true,
    });
    document.dispatchEvent(event);

    // Remove overlay from DOM (only for popup mode)
    if (mode === "popup") {
      const overlays = document.querySelectorAll(
        "baluu-giftcard-checkout-overlay",
      );
      overlays.forEach((overlay) => {
        if (overlay.parentNode) {
          overlay.parentNode.removeChild(overlay);
        }
      });
    }
  }, [mode]);

  // Handle success
  const handleSuccess = useCallback(
    (purchase: any) => {
      const detail = {
        businessId,
        purchase,
        purchaseId: purchase?._id,
        total: purchase?.amount,
        currency: purchase?.currency,
      };

      const event = new CustomEvent("baluu:giftcard-purchased", {
        bubbles: true,
        composed: true,
        detail,
      });
      document.dispatchEvent(event);

      trackConversion("baluu_giftcard_purchased", {
        businessId,
        purchaseId: detail.purchaseId,
        total: detail.total,
        currency: detail.currency,
      });
    },
    [businessId],
  );

  // Handle error
  const handleError = useCallback(
    (error: Error | string) => {
      console.error("[GiftcardCheckout] Error:", error);
      const event = new CustomEvent("baluu:giftcard-checkout-error", {
        bubbles: true,
        composed: true,
        detail: {
          businessId,
          error: error instanceof Error ? error.message : error,
        },
      });
      document.dispatchEvent(event);
    },
    [businessId],
  );

  // Handle Stripe payment success
  const handleStripePaymentSuccess = useCallback(
    (data: SuccessData) => {
      setSuccessData({
        ...data,
        courseName: "Gift Card",
      });
      setPortalMode("success");

      const detail = {
        businessId,
        ...data,
      };

      const event = new CustomEvent("baluu:giftcard-purchased", {
        bubbles: true,
        composed: true,
        detail,
      });
      document.dispatchEvent(event);

      trackConversion("baluu_giftcard_purchased", detail);
    },
    [businessId],
  );

  // Handle Stripe portal close
  const handleStripeClose = useCallback(() => {
    setPortalMode("checkout");
    setStripePaymentData(null);
  }, []);

  // Handle success screen close
  const handleSuccessClose = useCallback(() => {
    handleClose();
  }, [handleClose]);

  // Handle opening terms dialog in Shadow DOM portal
  const handleOpenTermsDialog = useCallback((data: {
    termsSettings?: TermsSettings;
    customTerms?: CustomTerms;
    timezone?: string;
  }) => {
    setTermsDialogData({
      isOpen: true,
      ...data,
    });
  }, []);

  // Handle closing terms dialog
  const handleCloseTermsDialog = useCallback(() => {
    setTermsDialogData(prev => ({ ...prev, isOpen: false }));
  }, []);

  // The gift card as a basket line.
  const giftcardLine = useCallback(
    (itemData: GiftcardItemData): BasketLine => {
      const selectedImage = itemData.selectedImage || itemData.imageUrl || giftCardImages[0] || "";
      return {
        type: "giftcard",
        schoolId: businessId,
        schoolName: schoolName || businessId,
        // Neither the basket drawer nor the order summary has a
        // gift-card-specific label — both fall back to
        // `courseName || productName || "Item"`. The flow supplies
        // e.g. "£50.00 Gift Card"; without this the line read "Item",
        // disagreeing with the widget surface which already sets it.
        productName: itemData.productName,
        giftcardAmount: itemData.giftcardAmount ?? itemData.price,
        recipientEmail: itemData.recipientEmail ?? itemData.guestDetails?.email,
        // No `?? itemData.productName` fallback: that would put the LINE
        // LABEL ("£50.00 Gift Card") into the recipient's name. The flow's
        // validation makes recipientName mandatory, and the basket checkout
        // page collects it again for gift cards, so leaving it undefined is
        // both unreachable and recoverable — writing the wrong name is not.
        recipientName: itemData.recipientName,
        senderName: itemData.senderName,
        message: itemData.message,
        quantity: 1,
        price: itemData.price ?? 0,
        currency: itemData.currency || "GBP",
        imageUrl: selectedImage,
      };
    },
    [businessId, schoolName, giftCardImages],
  );

  // "Add to basket" (the surface offers the basket): the card joins the
  // basket, which opens, and the visitor carries on browsing. Before B3 the
  // flow offered this on every embed while the basket was switched off, so
  // the card went into a basket no page showed, and the next "Proceed to
  // checkout" here charged it again with the new one.
  const handleAddToBasket = useCallback(
    (itemData: GiftcardItemData) => {
      if (basketMode === "off") return;
      const line = giftcardLine(itemData);
      const outcome = addPurchaseToBasket({
        line,
        businessId,
        apiUrl,
        primaryColor,
        mode: basketMode,
        close: handleClose,
      });
      if (!outcome.ok) refuse([line]);
    },
    [basketMode, giftcardLine, businessId, apiUrl, primaryColor, handleClose, refuse],
  );

  // "Proceed to checkout": everything in the basket with it on; the card
  // alone (earlier lines for the school cleared) with it off. The overlay
  // goes once the basket checkout has it.
  const handleProceedToBasketCheckout = useCallback(
    (itemData: GiftcardItemData) => {
      const line = giftcardLine(itemData);
      const outcome = proceedToBasketCheckout({
        line,
        businessId,
        apiUrl,
        primaryColor,
        mode: basketMode,
        close: handleClose,
      });
      if (!outcome.ok) refuse([line]);
    },
    [giftcardLine, businessId, apiUrl, primaryColor, basketMode, handleClose, refuse],
  );

  // Wrapper styles based on mode
  const wrapperClassName =
    mode === "popup"
      ? "checkout-overlay-content flex items-start justify-center overflow-y-auto bg-black/50"
      : "w-full";

  const contentClassName =
    mode === "popup"
      ? "relative bg-background w-full max-w-lg mx-4 my-8 rounded-xl shadow-xl overflow-hidden"
      : "w-full";

  // Create a minimal product object for CheckoutGiftCard
  const giftCardProduct = {
    _id: `giftcard-${businessId}`,
    title: "Gift Card",
    name: "Gift Card",
  };

  // Create a minimal school object
  const school = {
    _id: businessId,
    slug: businessSlug,
  };

  return (
    <QueryClientProvider client={queryClient}>
      {/* Main checkout flow */}
      {portalMode === "checkout" && (
        <div
          className={wrapperClassName}
          onClick={(e) => {
            if (mode === "popup" && e.target === e.currentTarget) handleClose();
          }}
        >
          <div className={contentClassName}>
            <CheckoutGiftCard
              product={giftCardProduct}
              school={school}
              images={giftCardImages}
              onSuccess={handleSuccess}
              onError={handleError}
              onClose={mode === "popup" ? handleClose : undefined}
              showGiftcardDetailsStep={true}
              isEmbedMode={mode === "inline"}
              currency="GBP"
              termsSettings={termsSettings}
              customTerms={customTerms}
              onOpenTermsDialog={handleOpenTermsDialog}
              basketItemCount={basketItemCount}
              basketConflict={basketConflict}
              onStepChange={handleStepChange}
              onAddToBasket={basketMode === "off" ? undefined : handleAddToBasket}
              onProceedToBasketCheckout={handleProceedToBasketCheckout}
              directCheckout={basketMode === "off"}
            />
          </div>
        </div>
      )}

      {/* Stripe Payment Portal - rendered via portal to document.body (outside Shadow DOM) */}
      {stripePaymentData && (
        <StripePaymentPortal
          isOpen={portalMode === "stripe"}
          paymentData={stripePaymentData}
          stripePublicKey={stripePaymentData.stripePublicKey}
          primaryColor={primaryColor}
          onSuccess={handleStripePaymentSuccess}
          onClose={handleStripeClose}
        />
      )}

      {/* Success Screen Portal - rendered via portal to document.body (outside Shadow DOM) */}
      {successData && (
        <SuccessScreenPortal
          isOpen={portalMode === "success"}
          successData={successData}
          primaryColor={primaryColor}
          onClose={handleSuccessClose}
        />
      )}

      {/* Terms dialog rendered in Shadow DOM portal for proper styling */}
      <TermsDialogPortal
        isOpen={termsDialogData.isOpen}
        onClose={handleCloseTermsDialog}
        settings={termsDialogData.termsSettings}
        customTerms={termsDialogData.customTerms}
        primaryColor={primaryColor}
      />
    </QueryClientProvider>
  );
}
